import React from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { ActivitySection } from 'src/modules/shared/components/molecules/activity-section';
import { EventTimeline } from 'src/modules/shared/components/molecules/event-timeline';
import type {
  TCPropertyImportPropertyScope,
  TCPropertyImportSyncMissingPolicy,
  IntegrationConnectedAccountResponse,
} from 'src/api/generated';
import type { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { useIntegrationActivityEvents } from 'src/modules/shared/hooks/api/integrations/integrations';
import type { MeliIntegrationSelectOption } from 'src/modules/settings/types/meli-integration-card.types';
import {
  buildIntegrationActivityEventTags,
  getIntegrationActivityEventLabelKey,
  resolveIntegrationActivityEventTone,
} from 'src/modules/shared/utils/integrations/integration-activity-event.util';
import {
  formatDate,
  formatTimeDistance,
} from 'src/modules/shared/utils/formatting/date.util';
import {
  isMeliIntegrationMode,
  type FrontendMeliIntegrationMode,
} from 'src/modules/shared/utils/integrations/meli-integration.util';

type SellerContactWhatsAppFieldProps = Pick<
  MeliIntegrationBodyProps,
  | 'canUpdate'
  | 'disconnecting'
  | 'loadingIntegrations'
  | 'sellerContactWhatsAppOptions'
  | 'sellerContactWhatsAppValue'
  | 'loadingWhatsAppPhones'
  | 'hasWhatsAppPhonesError'
  | 'hasNoWhatsAppPhones'
  | 'needsWhatsAppPhoneSelection'
  | 'isSellerContactWhatsAppStale'
  | 't'
  | 'onSellerContactWhatsAppChange'
>;

const SellerContactWhatsAppField: React.FC<SellerContactWhatsAppFieldProps> = ({
  canUpdate,
  disconnecting,
  loadingIntegrations,
  sellerContactWhatsAppOptions,
  sellerContactWhatsAppValue,
  loadingWhatsAppPhones,
  hasWhatsAppPhonesError,
  hasNoWhatsAppPhones,
  needsWhatsAppPhoneSelection,
  isSellerContactWhatsAppStale,
  t,
  onSellerContactWhatsAppChange,
}) => {
  const showStaleWarning =
    !loadingWhatsAppPhones &&
    !hasWhatsAppPhonesError &&
    isSellerContactWhatsAppStale;
  const showEmailOnlyHelp =
    hasNoWhatsAppPhones && !isSellerContactWhatsAppStale;
  const showSelectionWarning =
    !loadingWhatsAppPhones &&
    !hasWhatsAppPhonesError &&
    !isSellerContactWhatsAppStale &&
    needsWhatsAppPhoneSelection;
  const showHelp =
    !loadingWhatsAppPhones &&
    !hasWhatsAppPhonesError &&
    !hasNoWhatsAppPhones &&
    !isSellerContactWhatsAppStale &&
    !needsWhatsAppPhoneSelection;

  return (
    <div className="flex flex-col gap-2">
      <LabelSelect
        label={t('settings.integrations.meli.sellerContactWhatsApp.label')}
        labelPosition="top"
        options={sellerContactWhatsAppOptions}
        selectProps={{
          value: sellerContactWhatsAppValue,
          onChange: (event) =>
            onSellerContactWhatsAppChange(event.target.value),
          disabled:
            !canUpdate ||
            disconnecting ||
            loadingIntegrations ||
            loadingWhatsAppPhones ||
            hasWhatsAppPhonesError ||
            hasNoWhatsAppPhones,
        }}
      />
      {loadingWhatsAppPhones ? (
        <Text size="tiny" color="secondary">
          {t('settings.integrations.meli.sellerContactWhatsApp.loading')}
        </Text>
      ) : null}
      {hasWhatsAppPhonesError ? (
        <Alert
          variant="warning"
          size="md"
          message={t(
            'settings.integrations.meli.sellerContactWhatsApp.loadError'
          )}
        />
      ) : null}
      {showEmailOnlyHelp ? (
        <Text size="tiny" color="secondary">
          {t('settings.integrations.meli.sellerContactWhatsApp.emailOnly')}
        </Text>
      ) : null}
      {showStaleWarning ? (
        <Alert
          variant="warning"
          size="md"
          message={t(
            hasNoWhatsAppPhones
              ? 'settings.integrations.meli.sellerContactWhatsApp.stale'
              : 'settings.integrations.meli.sellerContactWhatsApp.staleWithConnectedAccounts'
          )}
        />
      ) : null}
      {showSelectionWarning ? (
        <Alert
          variant="warning"
          size="md"
          message={t(
            'settings.integrations.meli.sellerContactWhatsApp.required'
          )}
        />
      ) : null}
      {showHelp ? (
        <Text size="tiny" color="secondary">
          {t('settings.integrations.meli.sellerContactWhatsApp.help')}
        </Text>
      ) : null}
    </div>
  );
};

/**
 * Form and activity content rendered inside the MercadoLibre integration card.
 */
export interface MeliIntegrationBodyProps {
  /** Whether the user can read integrations. */
  canRead: boolean;
  /** Whether the MercadoLibre connection is active. */
  isConnected: boolean;
  /** Whether the user can update integrations. */
  canUpdate: boolean;
  /** Whether disconnect is in flight. */
  disconnecting: boolean;
  /** Whether integrations are still loading. */
  loadingIntegrations: boolean;
  /** Whether auto-assignment is enabled. */
  autoAssignEnabled: boolean;
  /** Current missing publication handling policy. */
  syncMissingPolicy: TCPropertyImportSyncMissingPolicy;
  /** Current imported property scope. */
  propertyScope: TCPropertyImportPropertyScope;
  /** Selected MercadoLibre mode. */
  mode: FrontendMeliIntegrationMode;
  /** Mode select options. */
  modeOptions: MeliIntegrationSelectOption[];
  /** Missing policy select options. */
  missingPolicyOptions: MeliIntegrationSelectOption[];
  /** Property scope select options. */
  propertyScopeOptions: MeliIntegrationSelectOption[];
  /** Connected WhatsApp phone select options. */
  sellerContactWhatsAppOptions: MeliIntegrationSelectOption[];
  /** Serialized stable identity of the selected WhatsApp phone. */
  sellerContactWhatsAppValue: string;
  /** Whether connected WhatsApp phones are loading. */
  loadingWhatsAppPhones: boolean;
  /** Whether connected WhatsApp phones could not be loaded. */
  hasWhatsAppPhonesError: boolean;
  /** Whether the tenant has no connected WhatsApp phones. */
  hasNoWhatsAppPhones: boolean;
  /** Whether multiple phones require an explicit selection. */
  needsWhatsAppPhoneSelection: boolean;
  /** Whether the persisted selection no longer exists. */
  isSellerContactWhatsAppStale: boolean;
  /** Live external account identity authorized by OAuth. */
  connectedAccount: IntegrationConnectedAccountResponse | null;
  /** Whether the live identity is loading. */
  loadingConnectedAccount: boolean;
  /** Whether the live identity could not be loaded. */
  hasConnectedAccountError: boolean;
  /** Integration activity timeline events. */
  integrationActivityEvents: ReturnType<
    typeof useIntegrationActivityEvents
  >['events'];
  /** Active UI language. */
  language: string;
  /** Translation function. */
  t: ReturnType<typeof useI18n>['t'];
  /** Called when the mode changes. */
  onModeChange: (mode: FrontendMeliIntegrationMode) => void;
  /** Called when auto-assignment changes. */
  onAutoAssignChange: (enabled: boolean) => void;
  /** Called when the missing policy changes. */
  onSyncMissingPolicyChange: (value: TCPropertyImportSyncMissingPolicy) => void;
  /** Called when the property scope changes. */
  onPropertyScopeChange: (value: TCPropertyImportPropertyScope) => void;
  /** Called when the public WhatsApp phone changes. */
  onSellerContactWhatsAppChange: (value: string) => void;
}

/**
 * Renders the MercadoLibre settings body, including activity timeline.
 */
export const MeliIntegrationBody: React.FC<MeliIntegrationBodyProps> = ({
  canRead,
  isConnected,
  canUpdate,
  disconnecting,
  loadingIntegrations,
  autoAssignEnabled,
  syncMissingPolicy,
  propertyScope,
  mode,
  modeOptions,
  missingPolicyOptions,
  propertyScopeOptions,
  sellerContactWhatsAppOptions,
  sellerContactWhatsAppValue,
  loadingWhatsAppPhones,
  hasWhatsAppPhonesError,
  hasNoWhatsAppPhones,
  needsWhatsAppPhoneSelection,
  isSellerContactWhatsAppStale,
  connectedAccount,
  loadingConnectedAccount,
  hasConnectedAccountError,
  integrationActivityEvents,
  language,
  t,
  onModeChange,
  onAutoAssignChange,
  onSyncMissingPolicyChange,
  onPropertyScopeChange,
  onSellerContactWhatsAppChange,
}) => {
  if (!canRead) {
    return (
      <Alert
        variant="warning"
        size="md"
        message={t('settings.integrations.permissions.readRequired')}
      />
    );
  }

  if (!isConnected) {
    return (
      <Text size="small" color="secondary">
        {t('settings.integrations.meli.disconnected.help')}
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <Text size="tiny" color="secondary">
          {t('settings.integrations.meli.connectedAccount.label')}
        </Text>
        {loadingConnectedAccount ? (
          <Text size="small">
            {t('settings.integrations.meli.connectedAccount.loading')}
          </Text>
        ) : connectedAccount ? (
          <div className="mt-1 flex flex-col gap-1">
            <Text size="small" weight="semibold">
              {connectedAccount.account.displayName}
            </Text>
            <Text size="tiny" color="secondary">
              {[
                connectedAccount.account.username
                  ? `@${connectedAccount.account.username}`
                  : null,
                `ID ${connectedAccount.account.externalId}`,
                connectedAccount.account.email,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          </div>
        ) : null}
        {hasConnectedAccountError ? (
          <Alert
            variant="warning"
            size="md"
            message={t('settings.integrations.meli.connectedAccount.loadError')}
          />
        ) : null}
        {connectedAccount && !connectedAccount.matchesConfiguredExternalId ? (
          <Alert
            variant="warning"
            size="md"
            message={t('settings.integrations.meli.connectedAccount.mismatch', {
              configuredId: connectedAccount.configuredExternalId ?? '—',
              currentId: connectedAccount.account.externalId,
            })}
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <LabelSelect
          label={t('settings.integrations.meli.mode.label')}
          labelPosition="top"
          options={modeOptions}
          selectProps={{
            value: mode,
            onChange: (event) => {
              if (isMeliIntegrationMode(event.target.value)) {
                onModeChange(event.target.value);
              }
            },
            disabled: !canUpdate || disconnecting || loadingIntegrations,
          }}
        />
        <Text size="tiny" color="secondary">
          {t('settings.integrations.meli.mode.description')}
        </Text>
      </div>

      {mode === 'PUBLISH' ? (
        <SellerContactWhatsAppField
          canUpdate={canUpdate}
          disconnecting={disconnecting}
          loadingIntegrations={loadingIntegrations}
          sellerContactWhatsAppOptions={sellerContactWhatsAppOptions}
          sellerContactWhatsAppValue={sellerContactWhatsAppValue}
          loadingWhatsAppPhones={loadingWhatsAppPhones}
          hasWhatsAppPhonesError={hasWhatsAppPhonesError}
          hasNoWhatsAppPhones={hasNoWhatsAppPhones}
          needsWhatsAppPhoneSelection={needsWhatsAppPhoneSelection}
          isSellerContactWhatsAppStale={isSellerContactWhatsAppStale}
          t={t}
          onSellerContactWhatsAppChange={onSellerContactWhatsAppChange}
        />
      ) : null}

      <LabelSwitch
        label={t('settings.integrations.meli.autoAssign.label')}
        checked={autoAssignEnabled}
        onChange={onAutoAssignChange}
        disabled={!canUpdate || disconnecting || loadingIntegrations}
        labelPosition="top"
        helperText={t('settings.integrations.meli.autoAssign.help')}
      />

      <div className="space-y-1">
        <LabelSelect
          label={t('settings.integrations.meli.syncMissingPolicy.label')}
          labelPosition="top"
          options={missingPolicyOptions}
          selectProps={{
            value: syncMissingPolicy,
            onChange: (event) =>
              onSyncMissingPolicyChange(
                event.target.value as TCPropertyImportSyncMissingPolicy
              ),
            disabled: !canUpdate || disconnecting || loadingIntegrations,
          }}
        />
        <Text size="tiny" color="secondary">
          {t('settings.integrations.meli.syncMissingPolicy.help')}
        </Text>
      </div>

      <div className="space-y-1">
        <LabelSelect
          label={t('settings.integrations.meli.propertyScope.label')}
          labelPosition="top"
          options={propertyScopeOptions}
          selectProps={{
            value: propertyScope,
            onChange: (event) =>
              onPropertyScopeChange(
                event.target.value as TCPropertyImportPropertyScope
              ),
            disabled: !canUpdate || disconnecting || loadingIntegrations,
          }}
        />
        <Text size="tiny" color="secondary">
          {t('settings.integrations.meli.propertyScope.help')}
        </Text>
      </div>

      <ActivitySection contentClassName="max-h-56">
        <EventTimeline
          items={integrationActivityEvents.map((event) => ({
            key: `${event.id}`,
            label: event.externalSourceId
              ? `${t(getIntegrationActivityEventLabelKey(event.type))} · ${event.externalSourceId}`
              : t(getIntegrationActivityEventLabelKey(event.type)),
            value: formatTimeDistance(
              new Date(event.createdAt),
              true,
              language
            ),
            valueTooltip: formatDate(event.createdAt),
            tone: resolveIntegrationActivityEventTone(event),
            tags: buildIntegrationActivityEventTags(event, t),
          }))}
          emptyLabel={t('settings.integrations.activity.empty')}
        />
      </ActivitySection>
    </div>
  );
};
